import React, { useEffect, useState } from 'react';
import { Folder, Search, FolderOpen, FileText, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApi } from './DashboardLayout';

export default function ProviderFoldersOverview() {
  const { apiFetch } = useApi();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pendingUploads, setPendingUploads] = useState([]);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    loadFolders();
    loadPending();
  }, []);

  const syncFolders = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await apiFetch('/api/downloads/provider-folders/init', { method: 'POST' });
      const d = await r?.json();
      if (d?.status) {
        setSyncResult(d);
        loadFolders();
      }
    } catch { /* */ }
    setSyncing(false);
  };

  const loadFolders = async () => {
    setLoading(true);
    const r = await apiFetch('/api/downloads/provider-folders');
    const d = await r?.json();
    if (Array.isArray(d)) setFolders(d);
    setLoading(false);
  };

  const loadPending = async () => {
    const r = await apiFetch('/api/downloads/provider-uploads/pending');
    const d = await r?.json();
    if (Array.isArray(d)) setPendingUploads(d);
  };

  const filtered = folders.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  // Build map: folder name -> pending count
  const pendingMap = {};
  pendingUploads.forEach(u => {
    const parts = u.path?.split('/');
    if (parts && parts.length >= 3) {
      const folderName = parts[2]; // /Anbieter/FolderName/...
      pendingMap[folderName] = (pendingMap[folderName] || 0) + 1;
    }
  });

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  return (
    <div data-testid="provider-folders-overview">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Anbieter-Dokumente</h2>
          <p>{folders.length} Anbieter-Ordner</p>
        </div>
        <button onClick={syncFolders} disabled={syncing}
          data-testid="sync-provider-folders-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', fontSize: 13, fontWeight: 600,
            border: '1px solid var(--border)', borderRadius: 8,
            background: 'var(--bg-primary)', color: 'var(--text-primary)',
            cursor: syncing ? 'wait' : 'pointer', opacity: syncing ? 0.6 : 1,
          }}>
          <RefreshCw size={14} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
          {syncing ? 'Synchronisiere...' : 'Alle Ordner synchronisieren'}
        </button>
      </div>

      {syncResult && (
        <div style={{
          padding: '10px 14px', marginBottom: 14, borderRadius: 8, fontSize: 13,
          background: syncResult.errors?.length ? '#FFF7ED' : '#F0FDF4',
          border: `1px solid ${syncResult.errors?.length ? '#FDBA74' : '#86EFAC'}`,
          color: syncResult.errors?.length ? '#9A3412' : '#166534',
        }} data-testid="sync-result">
          {syncResult.message}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', maxWidth: 400 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="of-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Anbieter suchen..." style={{ paddingLeft: 36 }}
            data-testid="provider-folder-search" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {filtered.map(f => {
          // Extract provider ID from folder name (Name_ClubID)
          const parts = f.name.split('_');
          const clubId = parts[parts.length - 1];

          return (
            <Link key={f.path} to={f.provider_id ? `/app/provider-documents/${f.provider_id}` : '#'}
              style={{ textDecoration: 'none', color: 'inherit' }}
              data-testid={`folder-card-${f.name}`}>
              <div className="of-card" style={{
                padding: '16px', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
                display: 'flex', alignItems: 'center', gap: 12, position: 'relative',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--primary-alpha, rgba(204,0,0,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FolderOpen size={20} style={{ color: 'var(--primary)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-word', lineHeight: 1.4 }}>
                    {f.name.replace(/_([^_]+)$/, '')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    Club-ID: {clubId}
                  </div>
                </div>
                {pendingMap[f.name] > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, background: '#EF4444', color: '#fff',
                    padding: '2px 7px', borderRadius: 10, flexShrink: 0,
                  }}>
                    {pendingMap[f.name]} neu
                  </span>
                )}
                <ChevronRight size={16} style={{ color: 'var(--text-muted)', opacity: 0.4, flexShrink: 0 }} />
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          <Folder size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 13 }}>Keine Anbieter-Ordner gefunden</p>
        </div>
      )}
    </div>
  );
}
